import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Loader2, Plus, Zap } from 'lucide-react';
import MacroFormModal from './MacroFormModal';
import MacroRow from './MacroRow';
import { MACRO_QUERY_KEY } from '@/lib/crmMacros';

// The canned response library: what exists, what channel each suits, how often
// it is used and whether it is currently offered. The same list feeds every
// reply box in the admin area.
export default function MacroLibraryPanel() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState('');

  const { data: macros = [], isLoading } = useQuery({
    queryKey: MACRO_QUERY_KEY,
    queryFn: () => base44.entities.CrmMacro.list('sort_order', 200),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: MACRO_QUERY_KEY });

  const toggleActive = async (macro) => {
    setBusyId(macro.id);
    try {
      await base44.entities.CrmMacro.update(macro.id, { active: macro.active === false });
      await refresh();
    } finally {
      setBusyId('');
    }
  };

  const remove = async (macro) => {
    if (!window.confirm(`Delete “${macro.name}”? This cannot be undone.`)) return;
    setBusyId(macro.id);
    try {
      await base44.entities.CrmMacro.delete(macro.id);
      await refresh();
    } finally {
      setBusyId('');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-white text-sm font-bold">
            {macros.length} canned response{macros.length === 1 ? '' : 's'}
          </p>
          <p className="text-white/30 text-xs mt-0.5">
            Available to insert from any reply box — the case workspace, customer emails, the SMS
            inbox and the booking composers.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({})}
          className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-widest px-4 py-2.5 transition-colors shrink-0"
        >
          <Plus className="w-3.5 h-3.5" /> NEW RESPONSE
        </button>
      </div>

      <div className="hidden md:grid grid-cols-12 gap-3 px-4 text-white/25 text-[9px] font-bold tracking-widest">
        <span className="col-span-4">TITLE</span>
        <span className="col-span-2">CATEGORY</span>
        <span className="col-span-2">CHANNEL</span>
        <span className="col-span-1">USES</span>
        <span className="col-span-2">ACTIVE</span>
        <span className="col-span-1" />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      ) : macros.length === 0 ? (
        <div className="text-center py-16 border border-white/5 bg-[#111115]">
          <Zap className="w-8 h-8 text-white/10 mx-auto mb-3" />
          <p className="text-white/30 text-sm">No canned responses yet.</p>
          <p className="text-white/20 text-xs mt-1">
            Add one and it appears in the reply boxes straight away.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {macros.map((macro) => (
            <MacroRow
              key={macro.id}
              macro={macro}
              busy={busyId === macro.id}
              onEdit={setEditing}
              onToggle={toggleActive}
              onDelete={remove}
            />
          ))}
        </div>
      )}

      {editing && (
        <MacroFormModal
          macro={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}